import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminDrivers, fetchAdminDriverTownCoverage } from '../services/api/admin';
import type { AdminDriverListItem, AdminDriverTownCoverage } from '../types/admin';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

function gitStatusLabel(status: string | null | undefined): string {
  if (status === 'VERIFIED') return 'GIT verified';
  if (status === 'PENDING_REVIEW') return 'GIT pending';
  if (status === 'REJECTED') return 'GIT rejected';
  if (status === 'NOT_COVERED') return 'No GIT';
  return 'GIT not declared';
}

function gitStatusPillClass(status: string | null | undefined): string {
  if (status === 'VERIFIED') return 'admin-pill admin-pill-success';
  if (status === 'PENDING_REVIEW') return 'admin-pill admin-pill-warning';
  return 'admin-pill';
}

function formatNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA').format(Number(value ?? 0));
}

function townKey(town: string, province?: string | null, country?: string | null): string {
  return JSON.stringify([town, province ?? '', country ?? '']);
}

function locationLabel(driver: AdminDriverListItem): string {
  return [driver.homeBaseTown, driver.homeBaseProvince, driver.homeBaseCountry]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(', ') || 'Operating town not declared';
}

const AdminDriversPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drivers, setDrivers] = useState<AdminDriverListItem[]>([]);
  const [townCoverage, setTownCoverage] = useState<AdminDriverTownCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [townsLoading, setTownsLoading] = useState(true);
  const [error, setError] = useState('');
  const [townsError, setTownsError] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    query: '',
    onboardingsStep: 'COMPLETED',
    onboardingReviewStatus: 'APPROVED',
    accountType: '',
    enabled: 'true',
    town: searchParams.get('town') ?? '',
    province: searchParams.get('province') ?? '',
    country: searchParams.get('country') ?? '',
  });

  const selectedTownKey = filters.town
    ? townKey(filters.town, filters.province, filters.country)
    : '';

  const approvedEnabledTotal = useMemo(
    () => townCoverage.reduce((total, row) => total + Number(row.approvedEnabledCount ?? 0), 0),
    [townCoverage],
  );

  useEffect(() => {
    let mounted = true;

    const loadTowns = async () => {
      setTownsLoading(true);
      setTownsError('');
      try {
        const rows = await fetchAdminDriverTownCoverage({
          onboardingsStep: 'COMPLETED',
          onboardingReviewStatus: 'APPROVED',
          enabled: true,
          includeUnspecified: false,
        });
        if (mounted) setTownCoverage(rows.filter((row) => Number(row.approvedEnabledCount ?? 0) > 0));
      } catch (loadError: any) {
        if (mounted) setTownsError(loadError.message || 'Could not load represented towns.');
      } finally {
        if (mounted) setTownsLoading(false);
      }
    };

    void loadTowns();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchAdminDrivers({
          ...(filters.query ? { query: filters.query.trim() } : {}),
          ...(filters.onboardingsStep ? { onboardingsStep: filters.onboardingsStep } : {}),
          ...(filters.onboardingReviewStatus ? { onboardingReviewStatus: filters.onboardingReviewStatus } : {}),
          ...(filters.accountType ? { accountType: filters.accountType } : {}),
          ...(filters.enabled ? { enabled: filters.enabled === 'true' } : {}),
          ...(filters.town ? { town: filters.town } : {}),
          ...(filters.province ? { province: filters.province } : {}),
          ...(filters.country ? { country: filters.country } : {}),
          page,
          size: 12,
        });

        if (!mounted) return;
        setDrivers(response.items);
        setTotalPages(response.totalPages);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load drivers.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [filters, page]);

  const updateFilter = (name: 'query' | 'onboardingsStep' | 'onboardingReviewStatus' | 'accountType' | 'enabled', value: string) => {
    setPage(0);
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const updateTown = (value: string) => {
    const selected = townCoverage.find((row) => townKey(row.town, row.province, row.country) === value);
    const nextLocation = selected
      ? { town: selected.town, province: selected.province ?? '', country: selected.country ?? '' }
      : { town: '', province: '', country: '' };

    setPage(0);
    setFilters((current) => ({ ...current, ...nextLocation }));

    const nextParams = new URLSearchParams(searchParams);
    Object.entries(nextLocation).forEach(([key, locationValue]) => {
      if (locationValue) nextParams.set(key, locationValue);
      else nextParams.delete(key);
    });
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <AdminShell
      badge="Driver Directory"
      copy="Browse submitted and completed driver profiles, inspect account state, and jump into full review detail."
      title="Admin drivers"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}
      {townsError ? <div className="register-inline-error">{townsError}</div> : null}
      <section className="admin-panel">
        <div className="admin-driver-town-summary" aria-live="polite">
          <div>
            <strong>{townsLoading ? 'Loading towns' : `${formatNumber(approvedEnabledTotal)} drivers`}</strong>
            <span>
              {townsLoading
                ? 'Finding completed, approved and enabled drivers'
                : `completed, approved and enabled across ${formatNumber(townCoverage.length)} represented towns`}
            </span>
          </div>
          <div className="admin-driver-town-breakdown">
            {townCoverage.map((row) => (
              <span key={townKey(row.town, row.province, row.country)}>
                {row.town} <strong>{formatNumber(row.approvedEnabledCount)}</strong>
              </span>
            ))}
          </div>
        </div>
        <div className="admin-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => {
              updateFilter('query', event.target.value);
            }}
            placeholder="Search drivers by name, email, or phone"
            value={filters.query}
          />
          <select className="register-input" onChange={(event) => updateFilter('onboardingsStep', event.target.value)} value={filters.onboardingsStep}>
            <option value="">All steps</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select className="register-input" onChange={(event) => updateFilter('onboardingReviewStatus', event.target.value)} value={filters.onboardingReviewStatus}>
            <option value="">All review states</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <select className="register-input" onChange={(event) => updateFilter('accountType', event.target.value)} value={filters.accountType}>
            <option value="">All account types</option>
            <option value="INDIVIDUAL_OWNER">Individual owner</option>
            <option value="COMPANY_DRIVER">Company driver</option>
          </select>
          <select className="register-input" onChange={(event) => updateFilter('enabled', event.target.value)} value={filters.enabled}>
            <option value="">Enabled or disabled</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
          <select
            aria-label="Filter drivers by represented town"
            className="register-input admin-toolbar-wide"
            disabled={townsLoading || Boolean(townsError)}
            onChange={(event) => updateTown(event.target.value)}
            value={selectedTownKey}
          >
            <option value="">
              {townsLoading ? 'Loading represented towns...' : `All represented towns - ${formatNumber(approvedEnabledTotal)} drivers`}
            </option>
            {townCoverage.map((row) => (
              <option key={townKey(row.town, row.province, row.country)} value={townKey(row.town, row.province, row.country)}>
                {row.town} - {formatNumber(row.approvedEnabledCount)} {row.approvedEnabledCount === 1 ? 'driver' : 'drivers'}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <>
            <div className="admin-list">
              {drivers.map((driver) => (
                <div className="admin-list-card" key={driver.driverId}>
                  <strong>{driver.name}</strong>
                  <small>{driver.email}</small>
                  <small>
                    {driver.accountType || 'Pending account type'} • {driver.onboardingReviewStatus || 'No review state'}
                  </small>
                  <small>
                    Submitted {driver.onboardingSubmittedAt ? formatDateTime(driver.onboardingSubmittedAt) : 'not yet'}
                  </small>
                  <small className="admin-driver-location">{locationLabel(driver)}</small>
                  <div className="admin-pill-row">
                    <span className="admin-pill">{driver.onboardingsStep || 'Unknown step'}</span>
                    <span className="admin-pill">{driver.enabled ? 'Enabled' : 'Disabled'}</span>
                    <span className={gitStatusPillClass(driver.gitStatus)}>{gitStatusLabel(driver.gitStatus)}</span>
                    <span className={driver.riskMode === 'FULL_ACCESS' ? 'admin-pill admin-pill-success' : 'admin-pill admin-pill-warning'}>
                      {driver.riskMode === 'FULL_ACCESS' ? 'Full access' : 'Restricted'}
                    </span>
                    <span className="admin-pill">Total {formatCurrency(driver.totalEarnings)}</span>
                  </div>
                  <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                    <Link className="register-submit register-submit-inline" to={`/admin/drivers/${driver.driverId}`}>
                      Open review
                    </Link>
                  </div>
                </div>
              ))}
              {!drivers.length ? <p className="admin-empty">No drivers match the current admin filter set.</p> : null}
            </div>
            <div className="admin-pagination">
              <button className="register-secondary-button" disabled={page === 0} onClick={() => setPage((current) => Math.max(current - 1, 0))} type="button">Previous</button>
              <span>Page {page + 1} of {Math.max(totalPages, 1)}</span>
              <button className="register-secondary-button" disabled={page + 1 >= Math.max(totalPages, 1)} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
            </div>
          </>
        )}
        <p className="admin-muted-note" style={{ marginTop: '16px' }}>
          Account lock/enable mutations are not exposed here yet because the current mutation endpoints still use internal numeric IDs while list/detail contracts expose public UUIDs only.
        </p>
      </section>
    </AdminShell>
  );
};

export default AdminDriversPage;
